import React from "react";
import { Card, Col, Row, Tag, Tooltip } from "antd";
import {
  AppstoreOutlined,
  TeamOutlined,
  ToolOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  ThreeMSummary,
  normalizeRollup,
  fmtINR,
  fmtINRCompact,
  num,
} from "./threeMShared";
import { useFontScale } from "../../../../theme/fontScale";

// Accent palette matches the tree legend: Material blue, Manpower orange,
// Machinery purple (Phase/Activity/Task colors stay on the tree itself).
const CARD_DEFS = [
  {
    key: "material",
    title: "Material",
    icon: <AppstoreOutlined />,
    color: "#1677ff",
    background: "#f0f7ff",
    border: "#bae0ff",
  },
  {
    key: "manpower",
    title: "Manpower",
    icon: <TeamOutlined />,
    color: "#fa8c16",
    background: "#fff9f0",
    border: "#ffd591",
  },
  {
    key: "machinery",
    title: "Machinery",
    icon: <ToolOutlined />,
    color: "#722ed1",
    background: "#f9f4ff",
    border: "#d3adf7",
  },
] as const;

const SummaryCards: React.FC<{ summary?: ThreeMSummary }> = ({ summary }) => {
  const { fs } = useFontScale();
  const totals = normalizeRollup(summary?.totals);
  const taskCount = num(summary?.taskCount);
  const tasksWithNorms = num(summary?.tasksWithNorms);

  const amounts: Record<string, { amount: number; lines: number }> = {
    material: {
      amount: totals.materialAmount,
      lines: totals.materialLineCount,
    },
    manpower: {
      amount: totals.manpowerAmount,
      lines: totals.manpowerLineCount,
    },
    machinery: {
      amount: totals.machineryAmount,
      lines: totals.machineryLineCount,
    },
  };

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      {CARD_DEFS.map((def) => (
        <Col xs={24} sm={12} xl={6} key={def.key}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              background: def.background,
              border: `1px solid ${def.border}`,
              height: "100%",
            }}
            bodyStyle={{ padding: "12px 16px" }}
          >
            <div
              style={{
                fontSize: fs(12.5),
                fontWeight: 600,
                color: def.color,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {def.icon} {def.title}
            </div>
            <Tooltip title={fmtINR(amounts[def.key].amount)}>
              <div
                style={{
                  fontSize: fs(20),
                  fontWeight: 700,
                  color: "#1a1a2e",
                  marginTop: 6,
                  fontVariantNumeric: "tabular-nums",
                  cursor: "default",
                  width: "fit-content",
                }}
              >
                {fmtINRCompact(amounts[def.key].amount)}
              </div>
            </Tooltip>
            <div style={{ fontSize: fs(12), color: "#8c8c8c", marginTop: 2 }}>
              {amounts[def.key].lines} line
              {amounts[def.key].lines === 1 ? "" : "s"}
            </div>
          </Card>
        </Col>
      ))}
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={{
            borderRadius: 12,
            background: "#f6ffed",
            border: "1px solid #b7eb8f",
            height: "100%",
          }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div
            style={{
              fontSize: fs(12.5),
              fontWeight: 600,
              color: "#389e0d",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <WalletOutlined /> Total 3M Value
          </div>
          <Tooltip title={fmtINR(totals.totalAmount)}>
            <div
              style={{
                fontSize: fs(20),
                fontWeight: 700,
                color: "#1a1a2e",
                marginTop: 6,
                fontVariantNumeric: "tabular-nums",
                cursor: "default",
                width: "fit-content",
              }}
            >
              {fmtINRCompact(totals.totalAmount)}
            </div>
          </Tooltip>
          <div style={{ marginTop: 2 }}>
            <Tag
              color={tasksWithNorms > 0 ? "purple" : "default"}
              style={{ fontSize: fs(11), marginRight: 0 }}
            >
              {tasksWithNorms} of {taskCount} tasks norm-covered
            </Tag>
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default SummaryCards;
